import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TimelinePinnedEngagement from '../generic/TimelinePinnedEngagement';
import TimelineSourceEventWrapper from '../generic/TimelineSourceEventWrapper';
import TimelineDataErrorBannerV2 from './TimelineDataErrorBannerV2';
import TimelineEventContainer from './TimelineEventContainer';
import { EMPTY_STATE, ERROR_MESSAGE, JITA_MESSAGE, MONTH_LABEL, PINNED_ENGAGEMENT, SOURCE_EVENT } from './TimelineItemTypes';
import TimelineMonth from './TimelineMonth';
import TimelineRequestElevatedEmailPermissionsCardWrapperV2 from './TimelineRequestElevatedEmailPermissionsCardWrapperV2';
import TimelineZeroStateAdvancedFilters from './TimelineZeroStateAdvancedFilters';
import { useTimelineCollapsedContext } from 'crm-events-data';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file
import { isLoading } from 'crm_data/flux/LoadingStatus';
import { getEngagementOrInteractionType, useIsEventForcingIntersection } from 'crm-events-data';
import CompactEngagementSkeleton from 'crm-events-components/internal/skeletons/CompactEngagementSkeleton';
import { useElementDimensions } from 'crm-events-components/internal/observers/hooks/useElementDimensions';
import { useIsIntersecting } from 'crm-events-components/internal/observers/hooks/useIsIntersecting';
import { View } from 'foundations-components/transitional/components/View';
import { getTimelineScrollContainer } from '../../helpers/getTimelineScrollContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const INITIAL_EVENTS_AND_MONTH_BUFFER = 15;
function TimelineEventVirtualized({
  index,
  objectType,
  profileStartTime,
  subjectId,
  timelineItem,
  timelineLoadStartTime
}) {
  const timelineItemIdOrType = timelineItem.get('id') || timelineItem.get('timelineItemType');
  const {
    isForcingIntersection
  } = useIsEventForcingIntersection(timelineItemIdOrType);
  const [eventElement, setEventElement] = useState();
  const {
    elementDimensions: eventDimensions,
    resetElementDimensions: resetEventDimensions
  } = useElementDimensions({
    target: eventElement
  });

  // resets event height if expand/collapse all is toggled
  const isCollapsedByDefault = useTimelineCollapsedContext();
  const isCollapsedByDefaultValueLoading = useRef(true);
  useEffect(() => {
    if (isCollapsedByDefaultValueLoading.current) {
      isCollapsedByDefaultValueLoading.current = isLoading(isCollapsedByDefault);
      return;
    }
    resetEventDimensions();
  }, [isCollapsedByDefault, resetEventDimensions]);
  const scrollTargetRef = useRef(null);
  useEffect(() => {
    scrollTargetRef.current = getTimelineScrollContainer();
  }, []);
  const handleEventRef = useCallback(element => {
    setEventElement(element);
  }, []);
  const [targetElement, setTargetElement] = useState();
  const handleTargetRef = useCallback(element => {
    setTargetElement(element);
  }, []);
  const {
    isIntersecting
  } = useIsIntersecting({
    target: targetElement,
    initialIntersecting: index < INITIAL_EVENTS_AND_MONTH_BUFFER || isForcingIntersection,
    observerOptions: {
      root: scrollTargetRef.current,
      rootMargin: '100%'
    }
  });
  const renderedTimelineItem = useMemo(() => {
    const timelineItemType = timelineItem.get('timelineItemType');
    switch (timelineItemType) {
      case EMPTY_STATE:
        return /*#__PURE__*/_jsx(TimelineZeroStateAdvancedFilters, {
          noResults: true
        }, "filters-zero-state");
      case MONTH_LABEL:
        {
          const label = timelineItem.get('label');
          return /*#__PURE__*/_jsx(TimelineMonth, {
            isVirtualized: true,
            isFirstMonth: false,
            label: label,
            text: timelineItem.get('text')
          }, `month_${label}`);
        }
      case PINNED_ENGAGEMENT:
        return /*#__PURE__*/_jsx("div", {
          ref: handleEventRef,
          children: /*#__PURE__*/_jsx(TimelinePinnedEngagement, {
            objectType: objectType,
            pinnedEngagementId: timelineItem.get('pinnedEngagementId'),
            shouldDisplay: true,
            subjectId: subjectId
          })
        });
      case SOURCE_EVENT:
        return /*#__PURE__*/_jsx(TimelineSourceEventWrapper, {
          objectType: objectType,
          subjectId: subjectId,
          timelineItem: timelineItem
        }, "source-event");
      case ERROR_MESSAGE:
        return /*#__PURE__*/_jsx(TimelineDataErrorBannerV2, {
          failedOrTimedOutEvents: timelineItem.get('failedOrTimedOutEvents')
        }, "data-error-banner");
      case JITA_MESSAGE:
        return /*#__PURE__*/_jsx(TimelineRequestElevatedEmailPermissionsCardWrapperV2, {
          currentUserEmail: timelineItem.get('currentUserEmail'),
          portalDomain: timelineItem.get('portalDomain')
        }, "jita");
      default:
        return /*#__PURE__*/_jsx("div", {
          ref: handleEventRef,
          children: /*#__PURE__*/_jsx(TimelineEventContainer, {
            timelineItem: timelineItem,
            objectType: objectType,
            subjectId: subjectId,
            profileStartTime: profileStartTime,
            dataStartTime: timelineLoadStartTime
          }, timelineItemIdOrType)
        });
    }
  }, [handleEventRef, objectType, profileStartTime, subjectId, timelineItem, timelineItemIdOrType, timelineLoadStartTime]);
  const renderedVirtualizedTimelineItem = useMemo(() => {
    const shouldRenderSkeleton = !isIntersecting && !isForcingIntersection;
    return /*#__PURE__*/_jsx(_Fragment, {
      children: shouldRenderSkeleton ? /*#__PURE__*/_jsx(CompactEngagementSkeleton, {
        engagementType: getEngagementOrInteractionType({
          timelineItem
        }),
        heightOverride: eventDimensions.height,
        shouldUseMarginBottom: true
      }, timelineItemIdOrType) : renderedTimelineItem
    });
  }, [eventDimensions.height, isForcingIntersection, isIntersecting, renderedTimelineItem, timelineItem, timelineItemIdOrType]);
  if (timelineItemIdOrType === MONTH_LABEL || timelineItemIdOrType === EMPTY_STATE || timelineItemIdOrType === ERROR_MESSAGE) {
    return renderedTimelineItem;
  }
  return /*#__PURE__*/_jsx(View, {
    ref: handleTargetRef,
    "data-test-id": `${index}-intersection-observer`,
    paddingInline: "400",
    children: renderedVirtualizedTimelineItem
  });
}
export default TimelineEventVirtualized;